import React, { useEffect } from "react";
import { X } from "lucide-react";
import { fmtCurrency } from "../lib/formatters";
import { useElementSeries } from "../lib/useElementSeries";

// 2026-03 (Dave) — "Element Cost Over Time" modal, launched from the
// Elements tab header pill. Shows each element (ID + description) with
// the inflated cost across 30 forward years. Table is horizontally +
// vertically scrollable inside a fixed-size modal shell so any element
// count / horizon combination fits.
//
// Inflation convention matches the Projections screen: for year offset
// N (N = 1..30) the year label is `study_year + N - 1` and the
// extended cost inflates by `(1 + inflation) ** (N - 1)`. Year 1 (N=1)
// therefore renders the un-inflated baseline.
const YEARS = 30;

// Parse "358.02 EPDM Roof" → { id: "358.02", desc: "EPDM Roof" }.
// Falls back to the raw name when the element carries no numeric prefix.
const NAME_ID_RE = /^(\d+(?:\.\d+)?)\s+(.*)$/;
function splitNameId(name) {
  const m = (name || "").match(NAME_ID_RE);
  if (!m) return { id: "", desc: name || "" };
  return { id: m[1], desc: m[2] };
}

export default function ElementCostOverTimeModal({ association, variables, onClose }) {
  // Esc-to-dismiss so this modal behaves like every other modal in the
  // app.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const els = association?.elements || [];
  const studyYear = association?.study_year || new Date().getFullYear();
  const inflation = variables?.inflation_rate || 0;

  const yearIdx = Array.from({ length: YEARS }, (_, i) => i); // 0..29
  // Costs per year are server-computed (POST /element-series).
  const series = useElementSeries(association?.aid, variables);
  const costsById = Object.fromEntries(
    (series?.element_costs || []).map((r) => [r.element_id, r.costs]),
  );
  const cell = (costs, n) => (costs ? fmtCurrency(costs[n]) : "…");

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
      data-testid="element-cost-over-time-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white shadow-2xl rounded-[3px] flex flex-col"
        style={{
          width: "min(1400px, 96vw)",
          height: "min(820px, 92vh)",
          border: "1px solid #1C4E80",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b"
          style={{ borderColor: "#E8ECF1" }}
        >
          <div>
            <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
              Element Cost Over Time
            </div>
            <div className="text-[10.5px] text-slate mt-0.5">
              {els.length} elements · {YEARS}-year inflation projection at{" "}
              <span className="font-mono tabular-nums font-semibold text-navy">
                {(inflation * 100).toFixed(1)}%
              </span>{" "}
              per year
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate hover:text-coral p-1"
            aria-label="Close"
            data-testid="element-cost-over-time-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable table region — both axes. */}
        <div className="flex-1 overflow-auto">
          <table
            className="text-[11px] font-mono tabular-nums"
            style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}
          >
            <thead>
              <tr>
                <th
                  className="text-left px-2 py-1.5 font-bold uppercase tracking-[0.12em] text-[9.5px]"
                  style={{
                    position: "sticky", top: 0, left: 0, zIndex: 3,
                    background: "#1C4E80", color: "#fff",
                    minWidth: 90,
                  }}
                >
                  Element ID
                </th>
                <th
                  className="text-left px-2 py-1.5 font-bold uppercase tracking-[0.12em] text-[9.5px]"
                  style={{
                    position: "sticky", top: 0, left: 90, zIndex: 3,
                    background: "#1C4E80", color: "#fff",
                    minWidth: 260,
                    borderRight: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  Description
                </th>
                {yearIdx.map((n) => (
                  <th
                    key={n}
                    className="text-right px-2 py-1.5 font-bold uppercase tracking-[0.08em] text-[9.5px]"
                    style={{
                      position: "sticky", top: 0, zIndex: 2,
                      background: "#1C4E80", color: "#fff",
                      minWidth: 84,
                    }}
                  >
                    {studyYear + n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {els.map((e) => {
                const { id: parsedId, desc: parsedDesc } = splitNameId(e.name);
                const id = e.element_display_id || parsedId;
                const desc = parsedDesc || e.name || "";
                const costs = costsById[e.element_id];
                return (
                  <tr
                    key={e.element_id}
                    data-testid={`cost-over-time-row-${e.element_id}`}
                    className="hover:bg-navy-wash"
                  >
                    <td
                      className="px-2 py-1 text-ink"
                      style={{
                        position: "sticky", left: 0, zIndex: 1,
                        background: "#fff",
                        borderBottom: "1px solid #E8ECF1",
                      }}
                    >
                      {id}
                    </td>
                    <td
                      className="px-2 py-1 text-ink font-sans"
                      style={{
                        position: "sticky", left: 90, zIndex: 1,
                        background: "#fff",
                        borderBottom: "1px solid #E8ECF1",
                        borderRight: "1px solid #E8ECF1",
                        maxWidth: 260,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={desc}
                    >
                      {desc}
                    </td>
                    {yearIdx.map((n) => (
                      <td
                        key={n}
                        className="px-2 py-1 text-right text-ink"
                        style={{ borderBottom: "1px solid #E8ECF1" }}
                      >
                        {cell(costs, n)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            {/* Total row — pinned to the bottom of the scrollable region
                so the year sums stay visible while scrolling long
                inventories. */}
            <tfoot>
              <tr data-testid="cost-over-time-total-row">
                <td
                  className="px-2 py-1.5 font-bold uppercase tracking-[0.12em] text-[9.5px]"
                  style={{
                    position: "sticky", bottom: 0, left: 0, zIndex: 3,
                    background: "#1C4E80", color: "#fff",
                    borderTop: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  Total
                </td>
                <td
                  className="px-2 py-1.5 font-bold uppercase tracking-[0.12em] text-[9.5px]"
                  style={{
                    position: "sticky", bottom: 0, left: 90, zIndex: 3,
                    background: "#1C4E80", color: "#fff",
                    borderTop: "1px solid rgba(255,255,255,0.25)",
                    borderRight: "1px solid rgba(255,255,255,0.25)",
                  }}
                  data-testid="cost-over-time-total-count"
                >
                  Elements ({els.length})
                </td>
                {yearIdx.map((n) => {
                  // 2026-03 (Dave) — sum the ROUNDED per-row values
                  // instead of the exact products so the Total column
                  // is a ledger-honest sum of the numbers the user
                  // actually sees in the row above (matches Excel
                  // "sum of the column" behaviour).
                  return (
                    <td
                      key={n}
                      className="px-2 py-1.5 text-right font-bold"
                      style={{
                        position: "sticky", bottom: 0, zIndex: 2,
                        background: "#1C4E80", color: "#fff",
                        borderTop: "1px solid rgba(255,255,255,0.25)",
                      }}
                      data-testid={`cost-over-time-total-y${n + 1}`}
                    >
                      {cell(series?.cost_total_by_year, n)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer explainer + close */}
        <div
          className="flex items-center justify-between px-4 py-2 border-t text-[10.5px] text-slate"
          style={{ borderColor: "#E8ECF1" }}
        >
          <div>
            Extended cost (quantity × unit cost) is inflated by
            <span className="font-mono tabular-nums text-navy font-semibold mx-1">
              (1&nbsp;+&nbsp;{(inflation * 100).toFixed(1)}%)<sup>N−1</sup>
            </span>
            for column year <span className="font-mono tabular-nums">N</span>.
            Year 1 = baseline. Scroll horizontally for later years.
          </div>
          <button
            onClick={onClose}
            data-testid="element-cost-over-time-close-footer"
            className="border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1 text-[11px] font-semibold rounded-[3px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
